import axios, { AxiosInstance, AxiosError } from 'axios';
import { API_CONFIG, AUTH_CONFIG } from '@/config/api';

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.headers,
});

// Request interceptor - add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(AUTH_CONFIG.tokenKey);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle token refresh and errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as any;

    // If 500 error with OAuth/JWT issues, clear auth and redirect
    if (error.response?.status === 500) {
      const errorMessage = (error.response?.data as any)?.message || '';
      if (errorMessage.includes('token') || errorMessage.includes('OAuth') || errorMessage.includes('JWT')) {
        console.error('Token validation error - clearing auth data');
        localStorage.removeItem(AUTH_CONFIG.tokenKey);
        localStorage.removeItem(AUTH_CONFIG.refreshTokenKey);
        localStorage.removeItem(AUTH_CONFIG.userKey);
        window.location.href = '/login';
        return Promise.reject(error);
      }
    }

    // If 401 and not already retried
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem(AUTH_CONFIG.refreshTokenKey);
        
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        // Attempt to refresh token
        const { data } = await axios.post(`${API_CONFIG.baseURL}/refresh`, {
          refresh_token: refreshToken,
        });

        // Save new tokens
        localStorage.setItem(AUTH_CONFIG.tokenKey, data.access_token);
        if (data.refresh_token) {
          localStorage.setItem(AUTH_CONFIG.refreshTokenKey, data.refresh_token);
        }

        // Retry original request
        originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear tokens and redirect to login
        localStorage.removeItem(AUTH_CONFIG.tokenKey);
        localStorage.removeItem(AUTH_CONFIG.refreshTokenKey);
        localStorage.removeItem(AUTH_CONFIG.userKey);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
